import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Building2, FileText, DollarSign, Clock, AlertTriangle, CheckCircle, 
  Download, Eye, LogOut, ChevronRight, User, Mail, Phone, History,
  Calendar, CreditCard, Receipt, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const clientData = JSON.parse(localStorage.getItem('client_data') || '{}');
  const clientToken = localStorage.getItem('client_token');

  // Check for payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
      // Clear URL params
      navigate('/client-portal/dashboard', { replace: true });
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(
          `${API}/api/client-portal/payments/status/${sessionId}?token=${clientToken}`
        );
        
        if (response.data.payment_status === 'paid') {
          toast.success(`Payment of ${formatCurrency(response.data.amount)} successful!`);
          setCheckingPayment(false);
          loadDashboard();
          loadInvoices();
          navigate('/client-portal/dashboard', { replace: true });
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is being processed. Please refresh in a moment.');
          setCheckingPayment(false);
          navigate('/client-portal/dashboard', { replace: true });
        }
      } catch (err) {
        setCheckingPayment(false);
        navigate('/client-portal/dashboard', { replace: true });
      }
    };
    
    poll();
  };

  const initiatePayment = async (invoice) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(
        `${API}/api/client-portal/payments/create-checkout?token=${clientToken}`,
        {
          invoice_id: invoice.id,
          origin_url: window.location.origin
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  useEffect(() => {
    if (!clientToken) {
      navigate('/client-portal');
      return;
    }
    loadDashboard();
    loadInvoices();
  }, [clientToken]);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/client-portal/dashboard?token=${clientToken}`);
      setDashboard(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${API}/api/client-portal/invoices?token=${clientToken}`);
      setInvoices(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    }
  };

  const viewInvoiceDetail = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/api/client-portal/invoices/${invoiceId}?token=${clientToken}`);
      setSelectedInvoice(response.data);
      setShowInvoiceDetail(true);
    } catch (err) {
      toast.error('Failed to load invoice details');
    }
  };

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(
        `${API}/api/client-portal/invoices/${invoiceId}/pdf?token=${clientToken}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (err) {
      toast.error('Failed to download invoice');
    }
  };

  const exportPaymentHistory = async (format) => {
    try {
      const response = await axios.get(
        `${API}/api/client-portal/payments/export/${format}?token=${clientToken}`,
        { responseType: 'blob' }
      );
      
      const contentType = format === 'csv' ? 'text/csv' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_history.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Payment history exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to export payment history');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/client-portal/logout?token=${clientToken}`);
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    navigate('/client-portal');
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      overdue: 'bg-red-100 text-red-700 border-red-200',
      sent: 'bg-blue-100 text-blue-700 border-blue-200',
      draft: 'bg-slate-100 text-slate-700 border-slate-200',
      partially_paid: 'bg-amber-100 text-amber-700 border-amber-200',
      cancelled: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    const icons = {
      paid: <CheckCircle className="w-3 h-3" />,
      overdue: <AlertTriangle className="w-3 h-3" />,
      sent: <FileText className="w-3 h-3" />,
      partially_paid: <Clock className="w-3 h-3" />
    };
    return (
      <Badge variant="outline" className={`${styles[status] || styles.draft} flex items-center gap-1`}>
        {icons[status]}
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Utmost Financial</h1>
                <p className="text-xs text-slate-500">Client Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{clientData.name}</p>
                <p className="text-xs text-slate-500">{clientData.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500 hover:text-red-600" data-testid="client-logout-btn">
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          {['overview', 'invoices', 'payments'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab 
                  ? 'border-emerald-500 text-emerald-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Total Invoiced</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(dashboard.summary.total_invoiced)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-600 uppercase tracking-wide">Total Paid</p>
                      <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(dashboard.summary.total_paid)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-emerald-200 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 uppercase tracking-wide">Outstanding</p>
                      <p className="text-2xl font-bold text-amber-700 mt-1">{formatCurrency(dashboard.summary.total_outstanding)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`border-2 ${dashboard.summary.overdue_count > 0 ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs uppercase tracking-wide ${dashboard.summary.overdue_count > 0 ? 'text-red-600' : 'text-green-600'}`}>Overdue</p>
                      <p className={`text-2xl font-bold mt-1 ${dashboard.summary.overdue_count > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {dashboard.summary.overdue_count} invoice{dashboard.summary.overdue_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${dashboard.summary.overdue_count > 0 ? 'bg-red-200' : 'bg-green-200'}`}>
                      {dashboard.summary.overdue_count > 0 
                        ? <AlertTriangle className="w-5 h-5 text-red-600" />
                        : <CheckCircle className="w-5 h-5 text-green-600" />
                      }
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Invoices */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Recent Invoices</CardTitle>
                  <CardDescription>Your latest invoice activity</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('invoices')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.slice(0, 5).map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
                            <FileText className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{inv.invoice_number}</p>
                            <p className="text-xs text-slate-500">Due: {formatDate(inv.due_date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {getStatusBadge(inv.status)}
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">{formatCurrency(inv.balance_due)}</p>
                            <p className="text-xs text-slate-500">Balance</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => viewInvoiceDetail(inv.id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No invoices found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                All Invoices
              </CardTitle>
              <CardDescription>View and download your invoices</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono font-medium">{inv.invoice_number}</TableCell>
                      <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell>{formatDate(inv.due_date)}</TableCell>
                      <TableCell>{getStatusBadge(inv.status)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(inv.total)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(inv.balance_due)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {inv.can_pay && inv.balance_due > 0 && (
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => initiatePayment(inv)}
                              disabled={processingPayment}
                              title="Pay Now"
                              data-testid={`pay-invoice-${inv.id}`}
                            >
                              {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                              Pay
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => viewInvoiceDetail(inv.id)} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)} title="Download PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {invoices.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-lg font-medium">No Invoices</p>
                  <p className="text-sm">You don't have any invoices yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment History
                  </CardTitle>
                  <CardDescription>View your past payments</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPaymentHistory('csv')}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPaymentHistory('pdf')}
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Export PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {invoices.flatMap(inv => 
                  (inv.payments || []).map((p, idx) => ({
                    ...p,
                    invoice_number: inv.invoice_number,
                    key: `${inv.id}-${idx}`
                  }))
                ).sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || '')).map((payment) => (
                  <div key={payment.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Invoice {payment.invoice_number}</p>
                        <p className="text-sm text-slate-500">{formatDate(payment.payment_date)} • {payment.payment_method?.replace('_', ' ')}</p>
                        {payment.reference && <p className="text-xs text-slate-400">Ref: {payment.reference}</p>}
                      </div>
                    </div>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(payment.amount)}</p>
                  </div>
                ))}
                {invoices.flatMap(inv => inv.payments || []).length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <History className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-lg font-medium">No Payment History</p>
                    <p className="text-sm">Your payments will appear here.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Invoice Detail Modal */}
      <Dialog open={showInvoiceDetail} onOpenChange={setShowInvoiceDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice Details
            </DialogTitle>
            <DialogDescription>
              {selectedInvoice?.invoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Status & Dates */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  {getStatusBadge(selectedInvoice.invoice.status)}
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Invoice Date</p>
                  <p className="font-medium">{formatDate(selectedInvoice.invoice.invoice_date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="font-medium">{formatDate(selectedInvoice.invoice.due_date)}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.invoice.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tax ({selectedInvoice.invoice.tax_rate}%)</span>
                  <span>{formatCurrency(selectedInvoice.invoice.tax_amount)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.invoice.total)}</span>
                </div>
                {selectedInvoice.invoice.amount_paid > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Paid</span>
                      <span>-{formatCurrency(selectedInvoice.invoice.amount_paid)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg text-amber-600">
                      <span>Balance Due</span>
                      <span>{formatCurrency(selectedInvoice.balance_due)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Payment History */}
              {selectedInvoice.payments?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Payment History</h4>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((p, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                        <div>
                          <p className="font-medium text-emerald-700">{formatDate(p.payment_date)}</p>
                          <p className="text-sm text-emerald-600">{p.payment_method?.replace('_', ' ')}</p>
                        </div>
                        <p className="font-bold text-emerald-700">{formatCurrency(p.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowInvoiceDetail(false)}>
                  Close
                </Button>
                {selectedInvoice.can_pay && selectedInvoice.balance_due > 0 && (
                  <Button 
                    onClick={() => initiatePayment(selectedInvoice.invoice)} 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={processingPayment}
                  >
                    {processingPayment ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CreditCard className="w-4 h-4 mr-2" /> Pay {formatCurrency(selectedInvoice.balance_due)}</>
                    )}
                  </Button>
                )}
                <Button onClick={() => downloadInvoicePdf(selectedInvoice.invoice.id, selectedInvoice.invoice.invoice_number)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-80">
            <CardContent className="p-6 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-emerald-500 animate-spin mb-4" />
              <h3 className="font-semibold text-lg">Processing Payment</h3>
              <p className="text-sm text-slate-500 mt-2">Please wait while we confirm your payment...</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
